import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Filter, ChevronLeft, ChevronRight, Trophy, Target, BarChart3, Calendar } from "lucide-react";
import { FunnelSimple, ListBullets } from "@phosphor-icons/react";
import AdminSection from "../components/admin/AdminSection";
import { BRAND } from "../lib/brand";

const API = process.env.REACT_APP_BACKEND_URL;

const SPORT_LABELS = {
  football: "Fútbol",
  nba: "NBA",
  mlb: "MLB",
  nfl: "NFL",
  nhl: "NHL",
};

const SPORT_COLORS = {
  football: "#10B981",
  nba: "#F59E0B",
  mlb: "#3B82F6",
  nfl: "#8B5CF6",
  nhl: "#06B6D4",
};

function SportBadge({ sport }) {
  const label = SPORT_LABELS[sport] || sport?.toUpperCase() || "—";
  const color = SPORT_COLORS[sport] || "#6B7280";
  return (
    <span
      className="inline-block text-[10px] font-black tracking-wider px-2 py-0.5 rounded-full"
      style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {label}
    </span>
  );
}

function ResultBadge({ status }) {
  if (status === "won") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-black tracking-wider px-2.5 py-1 rounded-full"
        style={{ background: "rgba(16,185,129,0.15)", color: "#10B981", border: "1px solid rgba(16,185,129,0.3)" }}>
        <TrendingUp size={10} /> GANADO
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-black tracking-wider px-2.5 py-1 rounded-full"
      style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.25)" }}>
      <TrendingDown size={10} /> PERDIDO
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col gap-1"
      style={{ background: "#0E0E13", border: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span className="text-xs font-semibold" style={{ color: "#A1A1AA" }}>{label}</span>
      </div>
      <div className="font-black" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "clamp(1.6rem, 5vw, 2.4rem)", color, letterSpacing: "-0.04em" }}>
        {value}
      </div>
      {sub && <div className="text-[11px]" style={{ color: "#71717A" }}>{sub}</div>}
    </div>
  );
}

export default function HistorialPage() {
  const [picks, setPicks]       = useState([]);
  const [summary, setSummary]   = useState(null);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [sport, setSport]       = useState("all");
  const [result, setResult]     = useState("all");

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (sport !== "all") params.append("sport", sport);
      if (result !== "all") params.append("result", result);
      const { data } = await axios.get(`${API}/api/public/picks/history?${params}`);
      setPicks(data.picks || []);
      setSummary(data.summary || null);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {
      setPicks([]);
    } finally {
      setLoading(false);
    }
  }, [page, sport, result]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [sport, result]);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d + (d.length === 10 ? "T12:00:00Z" : "")).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#07070A", color: "#E4E4E7" }}>
      {/* ── Header ── */}
      <div style={{ background: "linear-gradient(180deg, #0D0D11 0%, #07070A 100%)", borderBottom: "1px solid #1A1A24" }}>
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="text-[11px] font-black tracking-widest uppercase mb-3" style={{ color: "#D4AF37" }}>
            {BRAND.name.toUpperCase()}
          </div>
          <h1 className="font-black mb-2" style={{ fontSize: "clamp(1.8rem, 6vw, 3rem)", letterSpacing: "-0.03em" }}>
            Historial de <span style={{ color: "#D4AF37" }}>Rendimiento</span>
          </h1>
          <p className="text-sm max-w-lg" style={{ color: "#9CA3AF" }}>
            Registro transparente de todos nuestros picks pasados. Actualizados en tiempo real.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* ── Summary Cards ── */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-summary">
            <SummaryCard icon={Trophy}   label="Efectividad"        value={`${summary.win_rate}%`}  color="#D4AF37" sub="Efectividad total" />
            <SummaryCard icon={Target}   label="Picks totales"   value={summary.settled}          color="#10B981" sub="Pronósticos cerrados" />
            <SummaryCard icon={TrendingUp}   label="Ganados"     value={summary.won}              color="#10B981" sub="Picks correctos" />
            <SummaryCard icon={TrendingDown} label="Perdidos"    value={summary.lost}             color="#EF4444" sub="Picks incorrectos" />
          </div>
        )}

        {/* ── Filters ── */}
        <AdminSection icon={FunnelSimple} title="Filtros" subtitle="Filtra por deporte o resultado" color="violet">
          <div className="flex flex-wrap gap-3 items-center">

          {/* Sport filter */}
          <div className="flex flex-wrap gap-2">
            {["all", "football", "nba", "mlb", "nfl", "nhl"].map(s => (
              <button key={s} onClick={() => setSport(s)} data-testid={`filter-sport-${s}`}
                className="px-3 py-1.5 rounded-full text-xs font-bold transition-all"
                style={sport === s
                  ? { background: SPORT_COLORS[s] || "#D4AF37", color: "#000" }
                  : { background: "rgba(255,255,255,0.04)", color: "#888", border: "1px solid rgba(255,255,255,0.08)" }}>
                {s === "all" ? "Todos" : (SPORT_LABELS[s] || s.toUpperCase())}
              </button>
            ))}
          </div>

          <div className="w-px h-5 hidden sm:block" style={{ background: "#2A2A35" }} />

          {/* Result filter */}
          <div className="flex gap-2">
            {[
              { k: "all", l: "Todos" },
              { k: "won", l: "Ganados" },
              { k: "lost", l: "Perdidos" },
            ].map(r => (
              <button key={r.k} onClick={() => setResult(r.k)} data-testid={`filter-result-${r.k}`}
                className="px-3 py-1.5 rounded-full text-xs font-bold transition-all"
                style={result === r.k
                  ? { background: r.k === "won" ? "#10B981" : r.k === "lost" ? "#EF4444" : "#D4AF37", color: "#000" }
                  : { background: "rgba(255,255,255,0.04)", color: "#888", border: "1px solid rgba(255,255,255,0.08)" }}>
                {r.l}
              </button>
            ))}
          </div>

            <div className="ml-auto text-xs" style={{ color: "#71717A" }}>
              {total} pick{total !== 1 ? "s" : ""}
            </div>
          </div>
        </AdminSection>

        {/* ── Table (desktop) / Cards (mobile) ── */}
        <AdminSection icon={ListBullets} title="Resultados" subtitle="Registro transparente de picks cerrados" color="emerald">
        {loading ? (
          <div className="text-center py-20" style={{ color: "#71717A" }}>Cargando...</div>
        ) : picks.length === 0 ? (
          <div className="text-center py-20" style={{ color: "#71717A" }}>
            <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
            <p>No hay picks con estos filtros.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-2xl overflow-hidden"
              style={{ background: "#0E0E13", border: "1px solid rgba(255,255,255,0.06)" }}>
              <table className="w-full" data-testid="history-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
                    {["Fecha", "Deporte", "Partido", "Predicción", "Cuota", "IQ", "Resultado"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-black tracking-widest uppercase"
                        style={{ color: "#71717A" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {picks.map((p, i) => (
                    <tr key={p.id || i} data-testid={`history-row-${i}`}
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}
                      className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-xs" style={{ color: "#A1A1AA" }}>
                        <div className="flex items-center gap-1.5">
                          <Calendar size={11} style={{ opacity: 0.5 }} />
                          {formatDate(p.match_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3"><SportBadge sport={p.sport} /></td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-white leading-tight">
                          {p.home_team}
                        </div>
                        <div className="text-[10px]" style={{ color: "#9CA3AF" }}>vs {p.away_team}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold text-white">{p.pick_selection}</div>
                        {p.market && <div className="text-[10px]" style={{ color: "#71717A" }}>{p.market}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono font-bold"
                        style={{ color: "#D4AF37" }}>
                        {p.odds ? p.odds.toFixed(2) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black"
                          style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.2)" }}>
                          {p.confidence || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3"><ResultBadge status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {picks.map((p, i) => (
                <div key={p.id || i} className="rounded-2xl p-4 space-y-2"
                  style={{ background: "#0E0E13", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center justify-between">
                    <SportBadge sport={p.sport} />
                    <ResultBadge status={p.status} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{p.home_team} vs {p.away_team}</div>
                    <div className="text-xs mt-0.5" style={{ color: "#D4AF37" }}>{p.pick_selection}</div>
                    {p.market && <div className="text-[10px] mt-0.5" style={{ color: "#71717A" }}>{p.market}</div>}
                  </div>
                  <div className="flex items-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
                    <span>{formatDate(p.match_date)}</span>
                    {p.odds && <span className="font-mono font-bold" style={{ color: "#D4AF37" }}>@{p.odds.toFixed(2)}</span>}
                    {p.confidence && <span>IQ: {p.confidence}</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-2" data-testid="history-pagination">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                  style={{ background: "#0E0E13", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm" style={{ color: "#A1A1AA" }}>
                  Página {page} de {totalPages}
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                  style={{ background: "#0E0E13", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
        </AdminSection>
      </div>
    </div>
  );
}
